import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { trackViewItem } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import StarRating from "@/components/StarRating";
import { Loader2, Check, Zap, ShieldCheck, Clock, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [variantId, setVariantId] = useState(null);
  const [upsells, setUpsells] = useState([]);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariantId(r.data.variants?.[0]?.id);
      trackViewItem(r.data);
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Product not found. <Link to="/shop" className="text-amber font-semibold">Back to shop</Link></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;

  const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
  const upsellObjs = (product.upsells || []).filter((u) => upsells.includes(u.key));
  const unitPrice = variant.price + upsellObjs.reduce((s, u) => s + u.price, 0);

  const toggleUpsell = (key) => setUpsells((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);

  const selection = {
    product_slug: product.slug, product_name: product.name, image: product.images?.[0],
    variant_id: variant.id, variant_label: variant.label, upsell_keys: upsells,
    unit_price: unitPrice, quantity: 1, is_instant_download: product.is_instant_download,
  };

  const primaryAction = () => {
    if (product.is_instant_download) {
      addItem(selection);
      toast.success("Added to cart");
      navigate("/cart");
    } else {
      const preview = new URLSearchParams(window.location.search).get("preview");
      navigate(`/customize/${product.slug}${preview ? `?preview=${preview}` : ""}`, { state: { selection } });
    }
  };

  const addToCart = () => { addItem(selection); toast.success("Added to cart"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/shop" className="text-sm text-amber font-semibold">← Back to shop</Link>
      <div className="lg:grid lg:grid-cols-12 lg:gap-12 mt-4">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="rounded-3xl overflow-hidden bg-white border border-slate-100 aspect-[4/3]">
            <img src={product.images[activeImg]} alt={product.name} data-testid="product-main-image" className="w-full h-full object-cover" />
          </div>
          <div className="flex gap-3 mt-3">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`rounded-xl overflow-hidden w-20 h-20 border-2 ${activeImg === i ? "border-amber" : "border-transparent"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Before / after */}
          {product.requires_photos && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-display text-lg font-bold text-slateink mb-4">Before &amp; after</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><img src={product.images[0]} alt="Before" className="rounded-xl w-full aspect-square object-cover" /><p className="text-xs font-semibold text-center text-slate-500 mt-2">YOUR PHOTO</p></div>
                <div><img src={product.images[Math.min(1, product.images.length - 1)]} alt="After" className="rounded-xl w-full aspect-square object-cover grayscale contrast-125" /><p className="text-xs font-semibold text-center text-amber-700/70 mt-2">PERSONALIZED ILLUSTRATION</p></div>
              </div>
            </div>
          )}
        </div>

        {/* Options */}
        <div className="lg:col-span-5 mt-8 lg:mt-0">
          <div className="lg:sticky lg:top-24">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700/70">{product.category}</p>
            <h1 className="font-display text-3xl font-extrabold text-slateink mt-1">{product.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              {product.avg_rating && <><StarRating value={product.avg_rating} size={16} /><span className="text-sm text-slate-500">{product.avg_rating} ({product.review_count})</span></>}
            </div>
            <p className="text-slate-600 mt-4 leading-relaxed">{product.description}</p>

            <div className="flex items-center gap-4 mt-5 text-sm">
              <span className="flex items-center gap-1.5 text-slate-600"><Clock className="w-4 h-4 text-amber" /> {product.delivery}</span>
              {product.is_instant_download
                ? <span className="flex items-center gap-1.5 text-sage font-semibold"><Zap className="w-4 h-4" /> Instant download</span>
                : <span className="flex items-center gap-1.5 text-slate-600"><ShieldCheck className="w-4 h-4 text-sage" /> Private &amp; secure</span>}
            </div>

            {/* Variants */}
            <div className="mt-6">
              <p className="font-semibold text-slateink text-sm mb-2">Choose your option</p>
              <div className="space-y-2" data-testid="variant-options">
                {product.variants.map((v) => (
                  <button key={v.id} onClick={() => setVariantId(v.id)} data-testid={`variant-${v.id}`}
                    className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-left transition-colors ${variantId === v.id ? "border-amber bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className="font-medium text-slateink">{v.label}</span>
                    <span className="font-semibold text-slateink">${v.price}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Upsells */}
            {product.upsells?.length > 0 && (
              <div className="mt-6">
                <p className="font-semibold text-slateink text-sm mb-2">Add extras</p>
                <div className="space-y-2">
                  {product.upsells.map((u) => (
                    <label key={u.key} data-testid={`upsell-${u.key}`} className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-2.5 cursor-pointer hover:border-slate-300">
                      <Checkbox checked={upsells.includes(u.key)} onCheckedChange={() => toggleUpsell(u.key)} />
                      <span className="flex-1 text-sm text-slateink">{u.label}</span>
                      <span className="text-sm font-semibold text-slate-600">+${u.price}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Total</span>
                <span data-testid="product-total" className="font-display text-2xl font-bold text-slateink">${unitPrice.toFixed(2)}</span>
              </div>
              <Button onClick={primaryAction} data-testid="create-yours-btn" className="w-full rounded-full bg-amber hover:bg-amber-600 text-white font-semibold h-12 text-base">
                {product.is_instant_download ? <><Zap className="w-4 h-4 mr-1" /> Buy &amp; download</> : <>Create Yours <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              {!product.is_instant_download && (
                <Button onClick={addToCart} variant="outline" data-testid="add-to-cart-btn" className="w-full rounded-full mt-2 font-semibold border-slate-300">
                  <ShoppingBag className="w-4 h-4 mr-1" /> Add to cart
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* What's included */}
      {product.whats_included?.length > 0 && (
        <div className="mt-14 bg-white rounded-2xl border border-slate-100 p-7">
          <h3 className="font-display text-xl font-bold text-slateink mb-4">What's included</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {product.whats_included.map((w, i) => (
              <div key={i} className="flex items-center gap-2.5 text-slate-700"><span className="w-5 h-5 rounded-full bg-sage/10 text-sage flex items-center justify-center shrink-0"><Check className="w-3 h-3" /></span>{w}</div>
            ))}
          </div>
        </div>
      )}

      {/* FAQs */}
      {product.faqs?.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-2xl font-bold text-slateink mb-4">Frequently asked</h3>
          <Accordion type="single" collapsible className="bg-white rounded-2xl border border-slate-100 px-5">
            {product.faqs.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slateink hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Reviews */}
      <div className="mt-8">
        <h3 className="font-display text-2xl font-bold text-slateink mb-4">Customer reviews</h3>
        {product.reviews?.length ? (
          <div className="grid md:grid-cols-2 gap-4">
            {product.reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className="flex items-center justify-between">
                  <StarRating value={r.rating} size={14} />
                  {r.verified && <span className="text-[10px] font-semibold text-sage bg-sage/10 rounded-full px-2 py-0.5">Verified purchase</span>}
                </div>
                {r.title && <p className="font-semibold text-slateink mt-2">{r.title}</p>}
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{r.body}</p>
                <p className="text-xs text-slate-400 mt-3">— {r.author}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-500">No reviews yet. Be the first!</p>}
      </div>
    </div>
  );
}
